import { format } from "date-fns";
import { find } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppContext } from "@/app/context";
import { useActiveRegistryDisplayName } from "@/app/hooks/use-active-registry-display-name";
import { useAllFeatureToggleValues } from "@/app/hooks/use-feature-toggle/use-all-feature-toggle-values";
import { useTransactionFee } from "@/app/hooks/use-transaction-fee";
import { AppDateFormats, formatDate } from "@/app/lib/format-date";
import { getRegistryCodeForTransactionFee, SupportedLinkedRegistry } from "@/app/lib/transaction-fee-helpers";
import { useTenantsQuery } from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE } from "@/app/types/okta.types";

import { getProductAttributesAsDisplayAttributes } from "../order-modal";
import {
  ForwardTradeDetailsModalCustomProps,
  ForwardTradeDetailsSummaryAttributes,
} from "./forward-trade-details-modal.types";

export const useForwardTradeDetailsModal = ({
  tradeContract,
}: ForwardTradeDetailsModalCustomProps): ForwardTradeDetailsSummaryAttributes => {
  const { t } = useTranslation();
  const { data: tenantsData, loading: tenantsLoading } = useTenantsQuery();
  const { userAccountInfo } = useAppContext();
  const activeRegistryCode = tradeContract.registryCode as AggregatedRegistryCode;

  const {
    toggles: { enableCrossRegistryForwardTrading },
    loading: featureTogglesLoading,
  } = useAllFeatureToggleValues();

  const [currencySymbol] = useMemo(() => {
    const tenant = find(tenantsData?.tenants, { id: tradeContract.tenantId });
    return [tenant?.localisation?.currencySymbol ?? "$"];
  }, [tradeContract.tenantId, tenantsData]);

  const getTransactionFeeRegistryCode = () => {
    if (!enableCrossRegistryForwardTrading || activeRegistryCode !== ANY_REGISTRY_CODE) {
      return activeRegistryCode;
    }

    return (
      getRegistryCodeForTransactionFee(userAccountInfo?.linkedRegistries as SupportedLinkedRegistry[]) ??
      activeRegistryCode
    );
  };

  const { activeRegistryDisplayName, registriesQueryLoading } = useActiveRegistryDisplayName(activeRegistryCode);

  const { getAppliedFee, loading: feeLoading } = useTransactionFee({
    registryCode: getTransactionFeeRegistryCode(),
  });
  const fee = useMemo(() => getAppliedFee(tradeContract.position), [getAppliedFee, tradeContract.position]);

  const productAttributes = useMemo(() => {
    return getProductAttributesAsDisplayAttributes(t, {
      attributes: tradeContract.attributes,
      formattedAttributes: tradeContract.formattedAttributes,
      activeRegistryCode,
      activeRegistryDisplayName,
    });
  }, [t, activeRegistryDisplayName, activeRegistryCode, tradeContract]);

  const counterPartyAttributes = useMemo(
    () => [
      {
        label: "Name",
        value: tradeContract.counterAccount?.businessName ?? "-",
      },
      {
        label: "Email",
        value: tradeContract.counterAccount?.email ?? "-",
      },
    ],
    [tradeContract.counterAccount],
  );

  const orderSummaryAttributes = useMemo(
    () => [
      {
        label: "Order Number",
        value: tradeContract.tradeContractNumber,
      },
      {
        label: "Order Lodgement Date",
        value: format(new Date(tradeContract.createdDate ?? ""), AppDateFormats.AbbreviatedMonthFormat),
      },
      {
        label: "Trade Settlement Date",
        value: formatDate(tradeContract.tradeSettlementDate ?? "", {
          formatStyle: AppDateFormats.AbbreviatedMonthFormat,
          noTZ: true,
          noOffset: true,
        }),
      },
    ],
    [tradeContract],
  );

  const forwardSummaryAttributes = [
    {
      title: t("Product Attributes"),
      attributes: productAttributes,
    },
    ...(tradeContract.counterAccount
      ? [
          {
            title: t("Counter Party Details"),
            attributes: counterPartyAttributes,
          },
        ]
      : []),
  ];

  return {
    fee,
    loading: tenantsLoading || feeLoading || registriesQueryLoading || featureTogglesLoading,
    currencySymbol,
    forwardSummaryAttributes,
    orderSummaryAttributes,
  };
};
